import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook that applies the user's emoji as a cursor when on /app routes
 * @param {Object} user - The user object containing the emoji property
 */
export const useEmojiCursor = (user) => {
  const location = useLocation();

  useEffect(() => {
    // Only apply cursor on /app routes
    if (!location.pathname.startsWith('/app')) {
      // Remove cursor when not on /app routes
      document.body.style.cursor = '';
      // Also remove from html element
      document.documentElement.style.cursor = '';
      return;
    }

    // Check if emoji cursor is enabled in theme preferences (defaults to true if not set)
    const emojiCursorEnabled = user?.themePreferences?.emojiCursor !== false;
    
    // Check if user has an emoji and emoji cursor is enabled
    if (!user?.emoji || !emojiCursorEnabled) {
      document.body.style.cursor = '';
      document.documentElement.style.cursor = '';
      const existingStyle = document.getElementById('emoji-cursor-override');
      if (existingStyle) {
        existingStyle.remove();
      }
      return;
    }

    // Create a cursor from the emoji using SVG data URI
    const emoji = user.emoji;
    const fontSize = 28; // Cursor size
    const cursorSize = fontSize + 4; // Add padding
    
    // Create SVG with emoji - using proper XML encoding
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${cursorSize}" height="${cursorSize}" viewBox="0 0 ${cursorSize} ${cursorSize}">
  <text x="50%" y="50%" font-size="${fontSize}" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif">${emoji}</text>
</svg>`;

    // Convert SVG to data URI (URL encoded for better compatibility)
    const encodedSvg = encodeURIComponent(svg);
    const dataUri = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
    
    // Apply cursor to body and html elements
    // The hotspot is at the center of the emoji
    const hotspotX = cursorSize / 2;
    const hotspotY = cursorSize / 2;
    const cursorStyle = `url("${dataUri}") ${hotspotX} ${hotspotY}, auto`;
    
    document.body.style.cursor = cursorStyle;
    document.documentElement.style.cursor = cursorStyle;
    
    // Apply cursor globally, but allow specific elements (like inputs) to override
    const style = document.createElement('style');
    style.id = 'emoji-cursor-override';
    style.textContent = `
      * {
        cursor: url("${dataUri}") ${hotspotX} ${hotspotY}, auto;
      }
      /* Allow text inputs and textareas to show text cursor */
      input[type="text"],
      input[type="email"],
      input[type="password"],
      input[type="number"],
      input[type="search"],
      input[type="url"],
      textarea,
      [contenteditable="true"],
      [contenteditable="true"] * {
        cursor: text;
      }
      /* Allow buttons and links to show pointer */
      a, button, [role="button"] {
        cursor: url("${dataUri}") ${hotspotX} ${hotspotY}, pointer;
      }
      /* Keep drag and resize cursors */
      [draggable="true"] {
        cursor: url("${dataUri}") ${hotspotX} ${hotspotY}, grab;
      }
      [draggable="true"]:active {
        cursor: url("${dataUri}") ${hotspotX} ${hotspotY}, grabbing;
      }
    `;
    document.head.appendChild(style);

    // Cleanup function to reset cursor
    return () => {
      document.body.style.cursor = '';
      document.documentElement.style.cursor = '';
      const existingStyle = document.getElementById('emoji-cursor-override');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [user?.emoji, user?.themePreferences?.emojiCursor, location.pathname]);
};

